// utils/fileValidation.js

/**
 * File validation utility for handling image uploads and file size constraints
 * Centralizes validation logic used across multiple components
 */

// File size constants
export const FILE_SIZE_LIMITS = {
  MAX_5MB: 5 * 1024 * 1024, // 5MB in bytes
  MAX_10MB: 10 * 1024 * 1024, // 10MB in bytes
  MAX_1MB: 1 * 1024 * 1024, // 1MB in bytes
};

// Supported image types
export const IMAGE_TYPES = {
  JPEG: "image/jpeg",
  JPG: "image/jpg",
  PNG: "image/png",
  GIF: "image/gif",
  WEBP: "image/webp",
};

// Common image type arrays
export const COMMON_IMAGE_TYPES = [
  IMAGE_TYPES.JPEG,
  IMAGE_TYPES.JPG,
  IMAGE_TYPES.PNG,
  IMAGE_TYPES.GIF,
];

export const EXTENDED_IMAGE_TYPES = [...COMMON_IMAGE_TYPES, IMAGE_TYPES.WEBP];

// File extensions mapping
export const IMAGE_EXTENSIONS = {
  jpeg: IMAGE_TYPES.JPEG,
  jpg: IMAGE_TYPES.JPG,
  png: IMAGE_TYPES.PNG,
  gif: IMAGE_TYPES.GIF,
  webp: IMAGE_TYPES.WEBP,
};

/**
 * Validates if file type is an allowed image type
 * @param {File} file - The file to validate
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @returns {boolean} - True if file type is valid
 */
export const isValidImageType = (file, allowedTypes = COMMON_IMAGE_TYPES) => {
  if (!file || !file.type) return false;
  return allowedTypes.includes(file.type);
};

/**
 * Validates if file size is within the allowed limit
 * @param {File} file - The file to validate
 * @param {number} maxSize - Maximum file size in bytes (default: 5MB)
 * @returns {boolean} - True if file size is valid
 */
export const isValidFileSize = (file, maxSize = FILE_SIZE_LIMITS.MAX_5MB) => {
  if (!file || !file.size) return false;
  return file.size <= maxSize;
};

/**
 * Gets file extension from filename
 * @param {string} filename - The filename to extract extension from
 * @returns {string|null} - The file extension in lowercase or null
 */
export const getFileExtension = (filename) => {
  if (!filename || typeof filename !== "string") return null;
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : null;
};

/**
 * Validates file extension against allowed extensions
 * @param {string} filename - The filename to validate
 * @param {string[]} allowedExtensions - Array of allowed extensions
 * @returns {boolean} - True if extension is valid
 */
export const isValidExtension = (
  filename,
  allowedExtensions = ["png", "jpg", "jpeg", "gif"]
) => {
  const extension = getFileExtension(filename);
  return extension ? allowedExtensions.includes(extension) : false;
};

/**
 * Formats file size to human readable format
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted file size string
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

/**
 * Comprehensive file validation for images
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @param {string[]} options.allowedTypes - Allowed MIME types
 * @param {string[]} options.allowedExtensions - Allowed file extensions
 * @param {number} options.maxSize - Maximum file size in bytes
 * @param {boolean} options.requireFile - Whether file is required
 * @returns {Object} - Validation result with success status and errors
 */
export const validateImageFile = (file, options = {}) => {
  const {
    allowedTypes = COMMON_IMAGE_TYPES,
    allowedExtensions = ["png", "jpg", "jpeg", "gif"],
    maxSize = FILE_SIZE_LIMITS.MAX_5MB,
    requireFile = true,
  } = options;

  const result = {
    valid: true,
    errors: [],
    warnings: [],
    fileInfo: null,
  };

  // Check if file is provided
  if (!file) {
    if (requireFile) {
      result.errors.push("No file provided");
      result.valid = false;
    }
    return result;
  }

  // Create file info
  result.fileInfo = {
    name: file.name,
    size: file.size,
    type: file.type,
    sizeFormatted: formatFileSize(file.size),
    extension: getFileExtension(file.name),
  };

  // Validate file type
  if (!isValidImageType(file, allowedTypes)) {
    result.errors.push(
      `Invalid file type. Allowed types: ${allowedTypes.join(", ")}`
    );
    result.valid = false;
  }

  // Validate file size
  if (!isValidFileSize(file, maxSize)) {
    const maxSizeFormatted = formatFileSize(maxSize);
    const fileSizeFormatted = formatFileSize(file.size);
    result.errors.push(
      `File size must be less than ${maxSizeFormatted}. Current size: ${fileSizeFormatted}`
    );
    result.valid = false;
  }

  // Validate file extension
  if (!isValidExtension(file.name, allowedExtensions)) {
    result.errors.push(
      `Invalid file extension. Allowed extensions: ${allowedExtensions.join(
        ", "
      )}`
    );
    result.valid = false;
  }

  // Add warnings for edge cases
  if (file.size === 0) {
    result.warnings.push("File appears to be empty");
  }

  if (file.type === "" || !file.type) {
    result.warnings.push("File type could not be determined");
  }

  return result;
};

/**
 * Quick validation for cover images (common use case)
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result
 */
export const validateCoverImage = (file) => {
  return validateImageFile(file, {
    allowedTypes: COMMON_IMAGE_TYPES,
    allowedExtensions: ["png", "jpg", "jpeg", "gif"],
    maxSize: FILE_SIZE_LIMITS.MAX_5MB,
    requireFile: true,
  });
};

/**
 * Quick validation for profile images
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result
 */
export const validateProfileImage = (file) => {
  return validateImageFile(file, {
    allowedTypes: EXTENDED_IMAGE_TYPES,
    allowedExtensions: ["png", "jpg", "jpeg", "gif", "webp"],
    maxSize: FILE_SIZE_LIMITS.MAX_5MB,
    requireFile: true,
  });
};

/**
 * Validates payment receipt image
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result
 */
export const validatePaymentReceipt = (file) => {
  return validateImageFile(file, {
    allowedTypes: [IMAGE_TYPES.JPEG, IMAGE_TYPES.PNG, IMAGE_TYPES.JPG],
    allowedExtensions: ["jpg", "jpeg", "png"],
    maxSize: FILE_SIZE_LIMITS.MAX_5MB,
    requireFile: true,
  });
};

/**
 * Creates a file preview URL
 * @param {File} file - The file to create preview for
 * @returns {string|null} - Object URL or null if invalid
 */
export const createFilePreview = (file) => {
  if (!file || !isValidImageType(file)) return null;
  return URL.createObjectURL(file);
};

/**
 * Safely revokes object URL
 * @param {string} url - The object URL to revoke
 */
export const revokeFilePreview = (url) => {
  if (url && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Converts file to base64 data URL
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Promise that resolves to base64 data URL
 */
export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file provided"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

/**
 * File validation configuration presets
 */
export const VALIDATION_PRESETS = {
  COVER_IMAGE: {
    allowedTypes: COMMON_IMAGE_TYPES,
    allowedExtensions: ["png", "jpg", "jpeg", "gif"],
    maxSize: FILE_SIZE_LIMITS.MAX_5MB,
  },
  PROFILE_IMAGE: {
    allowedTypes: EXTENDED_IMAGE_TYPES,
    allowedExtensions: ["png", "jpg", "jpeg", "gif", "webp"],
    maxSize: FILE_SIZE_LIMITS.MAX_5MB,
  },
  PAYMENT_RECEIPT: {
    allowedTypes: [IMAGE_TYPES.JPEG, IMAGE_TYPES.PNG, IMAGE_TYPES.JPG],
    allowedExtensions: ["jpg", "jpeg", "png"],
    maxSize: FILE_SIZE_LIMITS.MAX_5MB,
  },
  DOCUMENT_ATTACHMENT: {
    allowedTypes: COMMON_IMAGE_TYPES,
    allowedExtensions: ["png", "jpg", "jpeg", "gif"],
    maxSize: FILE_SIZE_LIMITS.MAX_10MB,
  },
};

// Default export for convenience
export default {
  FILE_SIZE_LIMITS,
  IMAGE_TYPES,
  COMMON_IMAGE_TYPES,
  EXTENDED_IMAGE_TYPES,
  IMAGE_EXTENSIONS,
  VALIDATION_PRESETS,
  isValidImageType,
  isValidFileSize,
  getFileExtension,
  isValidExtension,
  formatFileSize,
  validateImageFile,
  validateCoverImage,
  validateProfileImage,
  validatePaymentReceipt,
  createFilePreview,
  revokeFilePreview,
  convertFileToBase64,
};
